import {
  types,
  getEnv,
  flow,
  resolveIdentifier,
  getRoot,
} from 'mobx-state-tree';

import RequestState from '../types/RequestState';
import Storage from '../models/Storage';
import StatefulStore from '../models/StatefulStore';

const normalizeMerchantUrl = (merchantUrl) =>
  merchantUrl == null || merchantUrl === '' ? null : merchantUrl;

const merchantRequestKey = (merchantUrl) =>
  String(normalizeMerchantUrl(merchantUrl));

const StorageStore = StatefulStore.named('StorageStore')
  .props({
    storages: types.optional(types.array(Storage), []),
    // Merchant associated with the current LOADED or ERROR outcome.
    loadedMerchantUrl: types.maybeNull(types.string),
  })
  .actions((self) => {
    const pendingStorageRequests = new Map();
    let desiredMerchantUrl = null;
    let hasEnteredClientPage = false;

    const ifShoppingCenter = () =>
      getRoot(self).configStore.siteConfig.isShoppingCenter;

    const baseApi = (endpoint) =>
      ifShoppingCenter() ? `shopping-center/${endpoint}` : `${endpoint}`;

    const isReadyFor = (merchantUrl) =>
      self.state === RequestState.LOADED &&
      normalizeMerchantUrl(self.loadedMerchantUrl) ===
        normalizeMerchantUrl(merchantUrl);

    const isFailedFor = (merchantUrl) =>
      self.state === RequestState.ERROR &&
      normalizeMerchantUrl(self.loadedMerchantUrl) ===
        normalizeMerchantUrl(merchantUrl);

    return {
      refreshStoragesOnPageEntry(
        merchantUrl,
        { isHydration = false, refresh = false } = {}
      ) {
        const skipInitialHydration = !hasEnteredClientPage && isHydration;
        hasEnteredClientPage = true;

        if (skipInitialHydration || !refresh) {
          return Promise.resolve();
        }

        return self.loadStorages(merchantUrl);
      },
      makeSureStoragesLoaded(merchantUrl) {
        desiredMerchantUrl = normalizeMerchantUrl(merchantUrl);
        if (isReadyFor(merchantUrl)) {
          return Promise.resolve();
        }

        const existing = pendingStorageRequests.get(
          merchantRequestKey(merchantUrl)
        );
        if (existing) {
          self.setLoading(true);
          return existing;
        }

        // Automatic preparation does not repeatedly retry errors.
        if (isFailedFor(merchantUrl)) {
          return Promise.resolve();
        }

        return self.loadStorages(merchantUrl);
      },
      loadStorages: flow(function* loadStorages(merchantUrl) {
        const normalized = normalizeMerchantUrl(merchantUrl);
        desiredMerchantUrl = normalized;
        const requestKey = merchantRequestKey(normalized);

        const existing = pendingStorageRequests.get(requestKey);
        if (existing) {
          self.setLoading(true);
          return yield existing;
        }

        let settle;
        const shared = new Promise((resolve, reject) => {
          settle = { resolve, reject };
        });
        // Concurrent waiters use `shared`; this invocation drives the request.
        shared.catch(() => {});
        pendingStorageRequests.set(requestKey, shared);

        self.setLoading(true);
        try {
          const storages = yield getEnv(self).apiWrapper.request(
            `${baseApi('storages')}`,
            { params: { merchantUrl } }
          );

          if (desiredMerchantUrl === normalized) {
            self.storages = storages;
            self.loadedMerchantUrl = normalized;
            self.setLoading(false);
          }
          settle.resolve(storages);
          return storages;
        } catch (e) {
          if (desiredMerchantUrl === normalized) {
            self.loadedMerchantUrl = normalized;
            self.setError(e);
          }
          settle.reject(e);
          throw e;
        } finally {
          pendingStorageRequests.delete(requestKey);
        }
      }),
    };
  })
  .views((self) => {
    return {
      findStorageById(storageId) {
        return resolveIdentifier(Storage, self.storages, storageId);
      },
      get pickupStorages() {
        return self.storages.filter((storage) => storage.is_pickup_storage);
      },
      get listingStorages() {
        return self.storages.filter((storage) => storage.show_on_store_listing);
      },
    };
  });

export default StorageStore;
